import PageTitle from "@/components/PageTitle";
import { ModalProvider } from "../../components/Modal/Modal";
import Table from "../../components/Table/Table";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import { defaultLinjeColumns } from "../../config/linjeConfig";
import { KursusLinjeNavHeader } from "../Kursus/KursusLinjeLayoutCommon";
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../../components/spinner/Spinner";
import {
  PagedLinjeQueryContext,
  defaultSetSelected,
} from "../../context/PagedLinjeQueryProvider";
import FiltersLinje from "../../components/filter/FiltersLinje";
import { QUERY_PAGE_SIZE } from "../../api/util";
import { IRowData, SortBy } from "../../interfaces/table";
import { PagedLinjeQueryField } from "../../interfaces/linje";
import useLocalStorageState from "use-local-storage-state";
import { linjeIndexSortOrderKey } from "../../util/localStorageKeys";
import { Notification, useToaster } from "rsuite";
import DownloadPendingIcon from "../../components/icons/DownloadPendingIcon";
import { downloadIndexTableToExcel } from "../../util/tableExportUtils";
import { useSelectAllErrorToast } from "../../hooks/useSelectAllErrorToast";

const defaultSortBy: Array<SortBy<PagedLinjeQueryField>> = [
  {
    field: "nr",
    direction: "asc",
  },
];

export default function Linje() {
  return (
    <>
      <PageTitle title="Linjeregister" />
      <div className="l-main-row">
        <FiltersLinje />
        <Main />
      </div>
    </>
  );
}

const Main = () => {
  const pagedLinjeQueryContext = useContext(PagedLinjeQueryContext);
  const {
    columns,
    error,
    loading,
    setActiveColumns,
    pagedResponse,
    query,
    page,
    setQuery,
    selection,
    selectAllError,
    clearSelectAllError,
  } = pagedLinjeQueryContext;

  const {
    addSelectedIds,
    removeSelectedIds,
    selectAll,
    selectedIds,
    clearSelection,
    lastSelectedId,
    setLastSelectedId,
  } = selection;
  const navigate = useNavigate();

  // for all your toasting purposes
  const toaster = useToaster();
  useSelectAllErrorToast(selectAllError, clearSelectAllError);

  const [sortBy, setSortBy] = useLocalStorageState<Array<SortBy>>(
    linjeIndexSortOrderKey,
    { defaultValue: defaultSortBy },
  );

  if (loading && !pagedResponse) {
    return <Spinner className="flex-grow-1" label="Henter data" />;
  }

  if (error) {
    return <div>noget gik galt</div>;
  }

  if (!pagedResponse) {
    return null;
  }

  const { fmulinjer } = pagedResponse;
  const { edges, pageInfo, totalCount } = fmulinjer;
  const { endCursor, startCursor, hasNextPage, hasPreviousPage } = pageInfo;

  const isAllSelected = totalCount === selectedIds.length;

  function downloadToExcel() {
    if (query) {
      toaster.push(
        <Notification>
          <div className="d-flex flex-row gap-3 align-items-center">
            <span className={`c-theme-icon c-theme-icon--linje`}>
              <DownloadPendingIcon />
            </span>
            Forbereder fil download
          </div>
        </Notification>,
        {
          placement: "bottomEnd",
          duration: 2500,
        },
      );

      downloadIndexTableToExcel(
        selectedIds,
        query,
        "fmulinjer",
        columns,
        "export-fmulinjer",
      );
    }
  }

  return (
    <main className="l-main__content">
      <KursusLinjeNavHeader
        type="linjer"
        pageInfo={pageInfo}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        totalCount={totalCount}
        onPreviousClick={() => {
          setQuery({
            ...query,
            page: page - 1,
            after: undefined,
            before: startCursor,
          });
        }}
        onNextClick={() => {
          setQuery({
            ...query,
            page: page + 1,
            before: undefined,
            after: endCursor,
          });
        }}
        page={page}
        allSelected={isAllSelected}
        selectedIds={selectedIds}
        downloadToExcel={downloadToExcel}
      />
      <div className="l-main__table px-2">
        <ModalProvider>
          <Table
            onRowClick={(data: IRowData) => {
              navigate(`/linje/${data.id}`);
            }}
            className="table--selectable"
            columns={columns}
            keyField="id"
            data={edges.map((e) => {
              return {
                ...e.node,
                selected: selectedIds.indexOf(e.node.id) > -1,
              };
            })}
            selectRow={{
              setSelected: (rowData, selected, shiftKeyPressed) => {
                defaultSetSelected(
                  rowData,
                  selected,
                  shiftKeyPressed,
                  edges,
                  lastSelectedId,
                  addSelectedIds,
                  removeSelectedIds,
                  setLastSelectedId,
                );
              },
              toggleSelectAllOnPage: (mode) => {
                if (mode === "all") {
                  addSelectedIds(edges.map((e) => e.node.id));
                } else {
                  removeSelectedIds(edges.map((e) => e.node.id));
                }
              },
              isAllSelected: isAllSelected,
              selectAll,
              clearSelection,
            }}
            pageInfo={{
              pageSize: QUERY_PAGE_SIZE,
              totalNumberOfRows: totalCount,
            }}
            sortings={{
              defaultSortBy: defaultSortBy,
              setSortBy: setSortBy,
              sortBy: sortBy,
            }}
          />
          <ColumnEditorModal
            columns={columns}
            setActiveColumns={setActiveColumns}
            defaultColumns={defaultLinjeColumns}
          />
        </ModalProvider>
      </div>
    </main>
  );
};
